import { ContextManager, createActions } from 'dreamstate';

import { Logger } from '@vimeo/logging';

import { environmentConfig } from '../../configs/EnvironmentConfig';
import { localStorageConfig } from '../../configs/LocalStorageConfig';
import { IStoreInitialState } from '../../types/store';
import { getLiveConfigValue } from '../../utils/general/configuration';
import {
  clearLogs,
  saveReportFile,
  toggleDebugState,
  toggleFileLoggingState,
  toggleLoggingState,
} from './DebugControlManager.actions';

export interface IDebugControlContext {
  debugActions: {
    saveReportFile(): void;
    setFileReporting(isEnabled: boolean, remember?: boolean): void;
    setLoggingState(isEnabled: boolean, remember?: boolean): void;
    setDebugState(isEnabled: boolean, remember?: boolean): void;
    clearLogs(): void;
  };
  isEnabled: boolean;
  isLoggingEnabled: boolean;
  isFileLoggingEnabled: boolean;
}

/**
 * Debug context manager for dev environment and feature-flagged production.
 * Allows to enable debugging/logging/saving reports.
 */
export class DebugControlManager extends ContextManager<IDebugControlContext> {
  public override context: IDebugControlContext = {
    debugActions: createActions({
      clearLogs: () => this.clearLogs(),
      setFileReporting: (isEnabled: boolean, remember?: boolean) =>
        this.toggleFileLoggingState(isEnabled, remember),
      setDebugState: (isEnabled: boolean, remember?: boolean) =>
        this.toggleDebugState(isEnabled, remember),
      setLoggingState: (isEnabled: boolean, remember?: boolean) =>
        this.toggleLoggingState(isEnabled, remember),
      saveReportFile: () => this.saveReportFile(),
    }),
    isEnabled: getLiveConfigValue(localStorageConfig.DEBUG.ENABLED, false),
    isLoggingEnabled: getLiveConfigValue(
      localStorageConfig.DEBUG.LOGGING,
      environmentConfig.IS_DEV,
    ),
    isFileLoggingEnabled: getLiveConfigValue(localStorageConfig.DEBUG.FILE_LOGGING, false),
  };

  public readonly log: Logger = new Logger('🌪️DBG');

  public constructor(initialState?: IStoreInitialState) {
    super();

    // Sync logger and debugging state.
    Logger.IS_CONSOLE_ENABLED = this.context.isLoggingEnabled;
    Logger.IS_MARKING_ENABLED = this.context.isLoggingEnabled;
    Logger.IS_FILE_ENABLED = this.context.isFileLoggingEnabled;

    this.log.info('DS debug module was constructed for current context');
    this.log.info('Initial state provision:', initialState);
  }

  /**
   * Toggle the current debugging state.
   *
   * @param isEnabled - whether debug should be enabled
   * @param remember - whether toggled state should be persistent (remembered in local storage)
   */
  public toggleDebugState(isEnabled: boolean, remember: boolean = true): void {
    return toggleDebugState(this, isEnabled, remember);
  }

  /**
   * Toggle the current logging state.
   *
   * @param isEnabled - whether logging should be enabled
   * @param remember - whether toggled logging state should be persistent (remembered in local storage)
   */
  public toggleLoggingState(isEnabled: boolean, remember: boolean = true): void {
    return toggleLoggingState(this, isEnabled, remember);
  }

  /**
   * Toggle the current file logging state.
   *
   * @param isEnabled - whether file logging should be enabled
   * @param remember - whether toggled file logging state should be persistent (remembered in local storage)
   */
  public toggleFileLoggingState(isEnabled: boolean, remember: boolean = true): void {
    return toggleFileLoggingState(this, isEnabled, remember);
  }

  /**
   * Save report file.
   * For using logs when asking for CS/QA report and do not have direct access.
   */
  public saveReportFile(): void {
    return saveReportFile(this);
  }

  /**
   * Clear file logs to capture only required ones.
   */
  public clearLogs(): void {
    return clearLogs(this);
  }
}
